import { useMemo, useState } from "react";
import { ButtonGroup, Button } from "react-bootstrap";
import type { PkActivity } from "../../../types/playerPk";
import "./pk.css";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 24 }, (_, h) => h);

type Mode = "kills" | "deaths";

const COLORS: Record<Mode, string> = {
  kills: "70, 130, 180",
  deaths: "255, 99, 132"
};

const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;

type Props = {
  activity: PkActivity;
};

// 7 x 24 grid of when the player fights, in ET (weekday 0 = Sunday). Scrolls
// inside its card on narrow screens.
const ActivityHeatmap = ({ activity }: Props) => {
  const [mode, setMode] = useState<Mode>("kills");
  const [active, setActive] = useState<{ day: number; hour: number } | null>(
    null
  );

  const grid = activity[mode];
  const max = useMemo(
    () => grid.reduce((m, row) => Math.max(m, ...row), 0),
    [grid]
  );
  const total = useMemo(
    () => grid.reduce((sum, row) => sum + row.reduce((a, b) => a + b, 0), 0),
    [grid]
  );

  const noun = mode === "kills" ? "kill" : "death";
  const count = active ? grid[active.day]?.[active.hour] ?? 0 : 0;

  return (
    <div className="pk-card">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <ButtonGroup size="sm" aria-label="Heatmap metric">
          {(["kills", "deaths"] as Mode[]).map((m) => (
            <Button
              key={m}
              variant={m === mode ? "info" : "outline-info"}
              aria-pressed={m === mode}
              onClick={() => setMode(m)}
            >
              {m === "kills" ? "Kills" : "Deaths"}
            </Button>
          ))}
        </ButtonGroup>
        <div className="epic-lb-muted">All times Eastern (ET)</div>
      </div>

      {total === 0 ? (
        <div className="text-center epic-lb-muted">
          No {mode} in this range.
        </div>
      ) : (
        <>
          <div className="pk-heatmap-scroll">
            <div
              className="pk-heatmap"
              role="table"
              aria-label={`${mode} by weekday and hour (ET)`}
            >
              <div />
              {HOURS.map((h) => (
                <div key={h} className="pk-heatmap-label">
                  {h}
                </div>
              ))}
              {DAYS.map((day, d) => (
                <div key={day} style={{ display: "contents" }} role="row">
                  <div className="pk-heatmap-label pk-heatmap-label--day">
                    {day}
                  </div>
                  {HOURS.map((h) => {
                    const value = grid[d]?.[h] ?? 0;
                    const alpha = value === 0 ? 0.05 : 0.18 + 0.82 * (value / max);
                    const isActive = active?.day === d && active.hour === h;
                    return (
                      <div
                        key={h}
                        role="cell"
                        tabIndex={-1}
                        className={`pk-heatmap-cell${
                          isActive ? " pk-heatmap-cell--active" : ""
                        }`}
                        style={{
                          backgroundColor: `rgba(${COLORS[mode]}, ${alpha})`
                        }}
                        title={`${day} ${hourLabel(h)} ET: ${value} ${noun}${
                          value === 1 ? "" : "s"
                        }`}
                        aria-label={`${day} ${hourLabel(h)} ET: ${value} ${noun}${
                          value === 1 ? "" : "s"
                        }`}
                        onMouseEnter={() => setActive({ day: d, hour: h })}
                        onMouseLeave={() => setActive(null)}
                        onClick={() => setActive({ day: d, hour: h })}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="pk-heatmap-caption mt-2" aria-live="polite">
            {active
              ? `${DAYS[active.day]} ${hourLabel(active.hour)} ET: ${count} ${noun}${
                  count === 1 ? "" : "s"
                }`
              : "Hour of day (ET) across, weekday down. Hover or tap a cell."}
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityHeatmap;
